import { Link } from 'react-router-dom';
import { ArrowUpRight, Play } from 'lucide-react';
import SectionMarker from '@/components/sections/SectionMarker';
import PullQuote from '@/components/sections/PullQuote';
import PageHero from '@/components/sections/PageHero';
import { PHOTOS, STAN_STRATEGY_URL, DOCUMENTARY } from '@/constants/content';
import { usePageMeta } from '@/lib/usePageMeta';
import { SEO } from '@/constants/seo';

export default function About() {
  usePageMeta({ ...SEO.about });
  return (
    <div className="bg-ivory">
      <section className="pt-40 lg:pt-56 pb-16 lg:pb-20" data-testid="about-origenne-intro">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 pb-16 lg:pb-20 border-b border-taupe/30">
          <div className="lg:col-span-5">
            <div className="eyebrow !text-gold mb-6">ORIGENNE&trade;</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] text-matte">
              Where It All <em className="italic text-taupe">Begins.</em>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-matte/75 leading-relaxed">
            <p>
              ORIGENNE is my personal Founder HQ, the digital home where the many dimensions of my
              life and work come together.
            </p>
            <p>
              Rooted in the idea of origin, creation, and new beginnings, it represents the journey
              behind what I build, the faith that grounds me, the experiences that shape me, and the
              vision that continues to unfold.
            </p>
            <p>
              This is where you can explore my story, my companies, my work, and what I&rsquo;m
              building next.
            </p>
          </div>
        </div>
        </div>
      </section>

      <section className="pt-8 lg:pt-12 pb-24 lg:pb-32">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow text-taupe mb-8">About · The Founder</div>
            <h1 className="font-serif-display text-6xl md:text-7xl lg:text-[8rem] leading-[0.94] tracking-tightest text-matte">
              A quiet architect
              <br />
              of <em className="italic text-taupe">modern ventures.</em>
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-matte/70 leading-relaxed text-[15px] max-w-md">
              Toia Wallace is a founder and technology entrepreneur composing an ecosystem
              of companies across software, healthcare, real estate, and media,
              each engineered to help entrepreneurs build with clarity.
            </p>
            <div className="mt-8 flex flex-wrap gap-x-4 gap-y-2">
              {['Founder', 'Speaker', 'Author', 'AI Business Transformation Strategist', 'Software Entrepreneur'].map((t) => (
                <span key={t} className="font-label text-[10px] tracking-widest uppercase text-matte/70">
                  {t}
                  <span className="ml-4 inline-block w-1 h-1 rounded-full bg-gold align-middle last:hidden" />
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-40">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <PageHero
            src={PHOTOS.aboutPage}
            alt="Toia Wallace"
            mobileAnchor="top"
            desktopAnchor="center"
            desktopHeight="md:h-[70vh] lg:h-[86vh]"
            testId="about-hero"
          />
        </div>
      </section>

      {/* WHERE IT ALL STARTED */}
      <section className="py-24 lg:py-32 bg-matte text-ivory relative overflow-hidden" data-testid="about-where-it-started">
        <div className="absolute inset-0 opacity-25 noise-grain pointer-events-none" aria-hidden />
        <div className="relative mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5">
            <div className="eyebrow text-gold mb-8">Where It All Started</div>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tightest leading-[1.02]">
              The documentary that <em className="italic text-gold">started it all.</em>
            </h2>
            <p className="mt-8 text-ivory/75 leading-[1.85] max-w-lg">
              Before building AI platforms, software companies, and global ventures, my
              entrepreneurial journey was documented by <em className="italic">CEOs In The City</em>.
              This documentary captures an earlier chapter of my story, from becoming a full-time
              entrepreneur to building multiple businesses and laying the foundation for everything
              I&rsquo;m creating today.
            </p>
            <p className="mt-6 font-serif-display italic text-2xl text-gold/90 max-w-lg leading-snug">
              Every new chapter began with the story you&rsquo;ll see here.
            </p>
            <div className="mt-10">
              <a href={DOCUMENTARY.url} target="_blank" rel="noreferrer" className="btn-primary bg-gold text-matte hover:bg-[#b8944e]" data-testid="about-cta-watch-documentary">
                <Play size={14} strokeWidth={1.6} />
                Watch Documentary
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </a>
            </div>
          </div>
          <div className="lg:col-span-7">
            <a
              href={DOCUMENTARY.url}
              target="_blank"
              rel="noreferrer"
              className="group relative block rounded-3xl overflow-hidden luxury-shadow"
              aria-label="Watch the CEOs In The City documentary"
            >
              <img
                src={DOCUMENTARY.thumbnail}
                onError={(e) => { e.currentTarget.src = DOCUMENTARY.thumbnailFallback; }}
                alt="CEOs In The City documentary preview"
                className="w-full aspect-video object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-matte/70 via-transparent to-transparent" aria-hidden />
              <div className="absolute inset-0 grid place-items-center">
                <span className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gold grid place-items-center transition-transform duration-500 group-hover:scale-110">
                  <Play size={30} strokeWidth={1.4} className="text-matte translate-x-0.5" />
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <SectionMarker number="01" label="Philosophy" />
            <h2 className="mt-10 font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] text-matte">
              Build companies the way a designer builds a house, with intention, in layers.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-matte/75 leading-relaxed">
            <p>
              I don&rsquo;t believe in building loudly. I believe in building durably. Every company
              inside this ecosystem is designed to solve one clear problem for one specific
              founder, and to compound over time.
            </p>
            <p>
              My work sits at the intersection of technology, artificial intelligence, and modern
              business strategy. It is quiet, considered, and unmistakably feminine, because the
              future of business belongs to founders who lead with taste as much as with data.
            </p>
            <p>
              I&rsquo;m not building a portfolio of services. I&rsquo;m building infrastructure, the kind
              that outlasts an era, and gives the next generation of entrepreneurs a place to
              stand.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-ivory">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid md:grid-cols-3 gap-10">
          {[
            { label: 'Mission', body: 'Give founders the technology, systems, and intelligence to build companies that endure.' },
            { label: 'Vision', body: 'A future where the most influential companies are quiet, considered, and founder-led.' },
            { label: 'Values', body: 'Clarity. Craft. Discipline. Beauty. Restraint. Discretion. Longevity.' },
          ].map((v, i) => (
            <div key={v.label} className="border-t border-taupe/30 pt-8">
              <div className="eyebrow text-taupe mb-4">{String(i + 1).padStart(2, '0')} · {v.label}</div>
              <p className="font-serif-display text-2xl md:text-3xl leading-snug tracking-tight text-matte">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <PullQuote attribution="Toia Wallace">
            I&rsquo;m not building services. I&rsquo;m building infrastructure.
          </PullQuote>
        </div>
      </section>

      <section className="py-24 lg:py-40 bg-matte text-ivory">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 text-center">
          <div className="eyebrow text-gold mb-8">Work Together</div>
          <h2 className="font-serif-display text-4xl md:text-6xl leading-tight tracking-tighter">
            If you&rsquo;d like personalized strategic guidance,<br className="hidden md:block" />
            book a <em className="italic text-gold">90-minute Strategy Intensive.</em>
          </h2>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <a
              href={STAN_STRATEGY_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="about-cta-strategy"
              className="btn-primary bg-gold text-matte hover:bg-[#b8944e]"
            >
              Book a Strategy Intensive
            </a>
            <Link
              to="/companies"
              data-testid="about-cta-companies"
              className="btn-primary bg-transparent text-ivory border border-ivory hover:bg-ivory hover:text-matte"
            >
              Explore the Companies
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
